// @flow
// This story is not interesting
/* eslint-disable ta-code-conventions/has-matching-storydoc */
import * as React from 'react';
import { requestTTICallback, cancelTTICallback } from '@ta/platform.tti';
import { Route, Environment } from '@ta/lithium.context';
import styles from './MonikerConfiguration.css';

/** These should match the set of all pixel types defined in pixels.ini.m4 and the mapping in FeatureRemarketingPixel.PixelType */
type PixelDescription =
  | {| +type: 'PAGEVIEW', +payload?: { +[key: string]: string } |}
  | {|
      +type: 'CLICKOUT',
      +payload?: {|
        +selectedPrice?: number,
        +offerStr?: string,
        +locId?: number,
        +contentId?: string,
        +isMetaCheckRates?: true,
        +attractionProductClickEventType?: 'checkAvailability',
        +obfuscatedAttractionProductCode?: string,
        +attractionProductPrice?: number,
        +attractionTravelersCount?: number,
      |},
    |}
  | {| +type: 'IB_CLICK', +payload?: {||} |}
  | {|
      +type: 'IB_ROOM_SELECT',
      +payload?: {|
        +selectedPrice?: string,
        +numAdults?: number,
        +numRooms?: number,
        +numChildren?: number,
        +totalStayPrice?: number,
      |},
    |}
  | {|
      +type: 'IB_CONFIRM',
      +payload?: {|
        +ibRoomPrice?: string,
        +ibCurrency?: string,
        +totalStayPrice?: number,
        +numAdults?: number,
        +numRooms?: number,
        +numChildren?: number,
        +converting?: boolean,
        +attractionTotalBookingPrice?: string,
        +selectedCurrency?: string,
        +shoppingCartProductCodes?: string,
        +transactionId?: string,
        +confirmationId?: string,
      |},
    |}
  | {| +type: 'LIGHTBOX_CLICK', +payload?: {||} |}
  | {|
      +type: 'LIGHTBOX_CLICKOUT',
      +payload?: {|
        +flight_totalvalue?: number,
        +vrInquiryId?: string,
      |},
    |}
  | {| +type: 'VR_CONFIRM', +payload?: {||} |}
  | {| +type: 'ENTER_DATES', +payload?: {||} |}
  | {| +type: 'LCB_POP', +payload?: {||} |}
  | {|
      +type: 'FLIGHTS_META',
      +payload?: {|
        +flight_totalvalue?: number,
      |},
    |}
  | {|
      +type: 'CARS_META',
      +payload?: {|
        +cars_meta_total_price?: number,
        +cars_meta_car_type?: string,
      |},
    |}
  | {|
      +type: 'CARS_SEARCH_RESULTS',
      +payload?: {|
        +cars_search_results_car_types?: { +[key: string]: string },
      |},
    |}
  | {| +type: 'PACKAGE_META', +payload?: {||} |}
  | {|
      +type: 'SEARCH',
      +payload?: {|
        flight_totalvalue?: number,
      |},
    |}
  | {| +type: 'CRUISE_META', +payload?: {||} |}
  | {| +type: 'HAC_PRICES_LOADED', +payload?: {||} |}
  | {|
      +type: 'RESTAURANTS_OFFLINE_ATTRIBUTION',
      +payload?: {|
        +locationId?: number,
        +eventName?: string,
      |},
    |}
  | {| +type: 'RESTAURANTS_PRODUCT_SELECTION', +payload?: {||} |}
  | {| +type: 'RESTAURANTS_PRODUCT_PAYMENT', +payload?: {||} |}
  | {| +type: 'RESTAURANTS_PRODUCT_CONVERSION', +payload?: {||} |}
  | {| +type: 'RESTAURANTS_NEW_REG_OWNER_FORM', +payload?: {||} |}
  | {| +type: 'RESTAURANTS_CONF_REG_OWNER', +payload?: {||} |}
  | {| +type: 'HOTEL_CLAIM_FORM_SUBMISSION', +payload?: {||} |}
  | {| +type: 'OFFER_VIEW', +payload?: {||} |}
  | {| +type: 'MEMBER_REGISTRATION_FORM', +payload?: {||} |}
  | {| +type: 'MEMBER_CREATE', +payload?: {||} |}
  | {| +type: 'SAVE_TO_TRIP_INTENT', +payload?: {||} |}
  | {| +type: 'SAVE_TO_TRIP', +payload?: {||} |}
  | {|
      +type: 'CREATE_TRIP',
      +payload?: {|
        +user_id?: string,
      |},
    |};

/** The list of pixels, derived from the descriptions */
export type PixelType = $PropertyType<PixelDescription, 'type'>;
/** The fully qualified configuration for a pixel */
export type PixelConfiguration = PixelType | PixelDescription;

/**
 * The load moniker callback function
 */
type LoadMoniker = (
  options: PixelConfiguration,
  onLoad?: (event: SyntheticEvent<HTMLIFrameElement>) => void,
) => mixed;

/** A context which exposes a callback to load a pixel */
export const Moniker = React.createContext<LoadMoniker | null>(null);

type Props = {|
  /** Allows a bootstrapped config to be passed in. This is used purely as a bridge and will indicate we are not on a Lithium page! */
  config?: {| servlet: string |},
  /** If true, then do not do sanity checking on the configuration */
  suppressConfigWarnings?: boolean,
  children: React.Node,
|};

const urlSequenceValueFilter = ([key, value]) => {
  if (typeof value === 'string' || typeof value === 'number') {
    return [[key, `${value}`]];
  }
  if (typeof value === 'boolean') {
    const stringyValue = value ? 'true' : 'false';
    return [[key, stringyValue]];
  }
  return [];
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'startLoadingMoniker':
      return { monikers: [...state.monikers, action.payload], loaded: state.loaded };
    case 'finishLoadingMoniker': {
      state.loaded.add(action.payload);
      return { monikers: state.monikers, loaded: state.loaded };
    }
    default:
      throw new Error('Attempted to dispatch an action with a type that does not exist');
  }
};

// Used to ensure monikers don't re-render children
// eslint-disable-next-line react/display-name
const PureSelector = React.memo<_>(({ children }) => children);

const MonikerConfiguration = ({ config, suppressConfigWarnings = false, children }: Props) => {
  const [state, dispatch] = React.useReducer(reducer, { monikers: [], loaded: new WeakSet() });

  const [{ path }, { uid }] = React.useContext(Route);
  const {
    locale: { code },
  } = React.useContext(Environment);
  const ttiCallbackIds = React.useRef(new Set());
  const debugCounter = React.useRef(0);
  const loadMoniker = React.useCallback(
    options => {
      debugCounter.current++;
      // eslint-disable-next-line ta-code-conventions/lithium-string-blacklist
      if (SERVER) {
        throw new Error('Stop! No! This callback should not be called on the server!');
      }
      const pixelType = typeof options === 'string' ? options : options.type;
      const payload = typeof options === 'string' ? {} : options.payload;

      const sequence = [
        ...Object.entries((payload: any)),
        ...Object.entries(config || {}),
        ['path', path !== '/Unknown' ? path : null],
        ['puid', uid],
        ['localeTag', code],
        ['pixelType', pixelType],
        ['lithium', !config],
      ].flatMap<[string, string]>(urlSequenceValueFilter);

      const src = `/PageMoniker?${new URLSearchParams(sequence).toString()}`;
      dispatch({ type: 'startLoadingMoniker', payload: [uid, pixelType, src] });
    },
    [config, path, uid, code],
  );

  React.useEffect(
    () => () => {
      ttiCallbackIds.current.forEach(id => cancelTTICallback(id));
    },
    [],
  );

  // Special debug hooks to ensure monikers are properly configured per page
  if (DEBUG) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    if (React.useContext(Moniker)) {
      throw new Error('Only one Moniker configuration should be defined!');
    }

    // eslint-disable-next-line react-hooks/rules-of-hooks
    React.useEffect(() => {
      const id = setTimeout(() => {
        if (debugCounter.current === 0 && !suppressConfigWarnings) {
          console.error(
            'A configuration was set but no monikers were loaded shortly after page load!',
          );
        }
      }, 1000 * 20);
      return () => {
        clearTimeout(id);
      };
    }, [suppressConfigWarnings]);
  }

  return (
    <>
      <Moniker.Provider value={loadMoniker}>
        <PureSelector>{children}</PureSelector>
      </Moniker.Provider>
      {state.monikers.map((moniker, index) => {
        const [puid, type, src] = moniker;
        if (state.loaded.has(moniker) && puid !== uid) {
          return null;
        }
        // eslint-disable-next-line no-restricted-globals
        if (DEBUG && typeof document === 'undefined') {
          throw new Error(
            'This should be all client-side! Did not expect document to not be defined!',
          );
        }
        return (
          // These iframes have traditionally never had titles as the content is meant to be hidden
          // eslint-disable-next-line jsx-a11y/iframe-has-title
          <iframe
            data-moniker-type={type}
            className={styles.moniker}
            src={src}
            // Index as key is fine since the list can only be appended, not shuffled/removed
            /* eslint-disable-next-line react/no-array-index-key */
            key={`${index}${src}`}
            onLoad={event => {
              // We guarantee image pixels are finisHed loading before the iframes can be removed
              dispatch({ type: 'finishLoadingMoniker', payload: moniker });
              // And tell pixels that should load after TTI when we're ready
              // Store as a variable since synthetic events are pooled and will return in the subsequent callback
              const iframeWindow = event.target.contentWindow;
              const ttiCallbackId = requestTTICallback(tti => {
                iframeWindow.postMessage(`TimeToInteractive:${tti || 'null'}`, '*');
                ttiCallbackIds.current.delete(ttiCallbackId);
              });
              ttiCallbackIds.current.add(ttiCallbackId);
            }}
          />
        );
      })}
    </>
  );
};

export default MonikerConfiguration;
